import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateResourcePricing,
  useUpdateResourcePricing,
  getListResourcePricingQueryKey,
  useListInventoryPlans,
} from "@workspace/api-client-react";
import type { ResourcePricing } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Phase 10 (Plan 10-07): SUBTYPES picker removed — replaced with planId picker driven by canonical 9 plans.

const CADENCES = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Six_Month", label: "Six Month" },
  { value: "Hourly", label: "Hourly" },
  { value: "Daily", label: "Daily" },
  { value: "Booking", label: "Booking" },
] as const;

const PAYMENT_UNITS = [
  { value: "Hour", label: "Hour" },
  { value: "Day", label: "Day" },
  { value: "Booking", label: "Booking" },
  { value: "Month", label: "Month" },
  { value: "Quarter", label: "Quarter" },
  { value: "Six_Month", label: "Six Month" },
] as const;

const NON_RECURRING_CADENCES = ["Hourly", "Daily", "Booking"];

function cadenceLabel(c: string): string {
  return CADENCES.find((cd) => cd.value === c)?.label ?? c;
}

const PricingFormSchema = z.object({
  planId: z.number({ required_error: "Plan is required" }).int().positive(),
  cadence: z.string().min(1, "Cadence is required"),
  paymentUnit: z.string().min(1, "Payment unit is required"),
  basePrice: z.number({ required_error: "Base price is required" }).min(0),
  securityDepositRequired: z.boolean().default(false),
  securityDepositAmount: z.number().nullable().optional(),
  discountPct: z.number().min(0).max(100).nullable().optional(),
  lockInMonths: z.number().min(0).nullable().optional(),
});

type PricingFormValues = z.infer<typeof PricingFormSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, editing existing row; when null, adding new */
  row: ResourcePricing | null;
}

export function EditPricingDialog({ open, onOpenChange, row }: Props) {
  const queryClient = useQueryClient();
  const createPricing = useCreateResourcePricing();
  const updatePricing = useUpdateResourcePricing();
  const isEditing = row !== null;

  // Phase 10 (Plan 10-07): planId picker replaces memberSubtype picker
  const { data: plans = [] } = useListInventoryPlans({});
  const planLabel = (id: number) => plans.find((p) => p.id === id)?.planName ?? `Plan #${id}`;

  const form = useForm<PricingFormValues>({
    resolver: zodResolver(PricingFormSchema),
    defaultValues: {
      planId: row?.planId ?? 0,
      cadence: row?.cadence ?? "",
      paymentUnit: row?.paymentUnit ?? "",
      basePrice: row ? Number(row.basePrice) : 0,
      securityDepositRequired: row?.securityDepositRequired ?? false,
      securityDepositAmount: row?.securityDepositAmount
        ? Number(row.securityDepositAmount)
        : null,
      discountPct: row?.discountPct ? Number(row.discountPct) : null,
      lockInMonths: row?.lockInMonths ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        planId: row?.planId ?? 0,
        cadence: row?.cadence ?? "",
        paymentUnit: row?.paymentUnit ?? "",
        basePrice: row ? Number(row.basePrice) : 0,
        securityDepositRequired: row?.securityDepositRequired ?? false,
        securityDepositAmount: row?.securityDepositAmount
          ? Number(row.securityDepositAmount)
          : null,
        discountPct: row?.discountPct ? Number(row.discountPct) : null,
        lockInMonths: row?.lockInMonths ?? null,
      });
    }
  }, [open, row, form]);

  const watchedCadence = form.watch("cadence");
  const watchedDeposit = form.watch("securityDepositRequired");
  const isNonRecurring = NON_RECURRING_CADENCES.includes(watchedCadence);

  const dialogTitle = isEditing
    ? `Edit Pricing — ${planLabel(row.planId)} / ${cadenceLabel(row.cadence)}`
    : "Add Pricing Row";

  async function onSubmit(values: PricingFormValues) {
    try {
      if (isEditing) {
        await updatePricing.mutateAsync({
          id: row.id,
          data: {
            basePrice: values.basePrice,
            securityDepositRequired: values.securityDepositRequired,
            securityDepositAmount: values.securityDepositAmount ?? null,
            discountPct: values.discountPct ?? null,
            lockInMonths: isNonRecurring ? null : (values.lockInMonths ?? null),
          },
        });
        toast.success("Pricing row updated.");
      } else {
        await createPricing.mutateAsync({
          data: {
            planId: values.planId,
            cadence: values.cadence as ResourcePricing["cadence"],
            paymentUnit: values.paymentUnit as ResourcePricing["paymentUnit"],
            basePrice: values.basePrice,
            securityDepositRequired: values.securityDepositRequired,
            securityDepositAmount: values.securityDepositAmount ?? null,
            discountPct: values.discountPct ?? null,
            lockInMonths: isNonRecurring ? null : (values.lockInMonths ?? null),
          },
        });
        toast.success("Pricing row added.");
      }
      queryClient.invalidateQueries({ queryKey: getListResourcePricingQueryKey() });
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed to save pricing: ${(err as Error).message ?? "Unknown error"}`);
    }
  }

  const isPending = createPricing.isPending || updatePricing.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Phase 10 (Plan 10-07): Plan picker replaces Subtype picker — planId is canonical. */}
            {isEditing ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Plan</p>
                <p className="text-sm">{planLabel(row.planId)}</p>
              </div>
            ) : (
              <FormField name="planId" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan <span className="text-destructive">*</span></FormLabel>
                  <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value ? String(field.value) : ""}>
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.planName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {/* Cadence — read-only when editing */}
            {isEditing ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Cadence</p>
                <p className="text-sm">{cadenceLabel(row.cadence)}</p>
              </div>
            ) : (
              <FormField name="cadence" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Cadence <span className="text-destructive">*</span></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Select cadence" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CADENCES.map((cd) => (
                        <SelectItem key={cd.value} value={cd.value}>{cd.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {/* Payment Unit — read-only when editing */}
            {isEditing ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Payment Unit</p>
                <p className="text-sm">{row.paymentUnit}</p>
              </div>
            ) : (
              <FormField name="paymentUnit" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Unit <span className="text-destructive">*</span></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PAYMENT_UNITS.map((pu) => (
                        <SelectItem key={pu.value} value={pu.value}>{pu.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {/* Base Price */}
            <FormField name="basePrice" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Base Price (₹) <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            {/* Security Deposit Required */}
            <FormField name="securityDepositRequired" control={form.control} render={({ field }) => (
              <FormItem className="flex items-center gap-2 space-y-0">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="font-normal">Security deposit required</FormLabel>
              </FormItem>
            )} />

            {/* Security Deposit Amount — shown only when Switch ON */}
            {watchedDeposit && (
              <FormField name="securityDepositAmount" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Deposit amount (₹)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {/* Discount % */}
            <FormField name="discountPct" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Discount vs Monthly (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">Discount compared to equivalent Monthly rate</p>
                <FormMessage />
              </FormItem>
            )} />

            {/* Lock-in Months — hidden for non-recurring cadences */}
            {!isNonRecurring && (
              <FormField name="lockInMonths" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Lock-in (months)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Discard
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : "Save Pricing"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
